import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActionResult, HelperFunctions, TrimPipe } from 'bowman-commons';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BowmanCacheService } from 'bowman-cache';
import { BowmanLoggerService } from 'bowman-logger';

import { FlowType } from '../model/flow-type';
import { FlowDto } from '../model/flow-dto';
import { DrawFlowDto, DrawFlowFullData } from '../model/drawflow-dto';
import { ClosurePath } from '../model/closure-path';
import { StepType } from '../model/step-type';
import { FlowValidation } from '../model/flow-validation';
import { FlowTestInstance, FlowTestObject } from '../model/flow-test-object';

@Injectable({
    providedIn: 'root'
})



export class FlowTypeEditorService {
    private _mainUrl: string = window.location.pathname + '/api/flowType/editor/';
    headers: HttpHeaders = new HttpHeaders();
    private _uuid: string;
    constructor(private _http: HttpClient,
        private _bowmanCacheService: BowmanCacheService,
        private _trimPipe: TrimPipe) {

        this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');
        this._uuid = HelperFunctions.uuid();
    }

    // GET
    public listFlowTypes(): Observable<ActionResult<Array<FlowType>>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'list');
        return this._http.get<ActionResult<Array<FlowType>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
    }

    public getLinegeList(): Observable<ActionResult<Record<string, any>>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'lineage/list');
        return this._http.get<ActionResult<Record<string, string>>>(dataUrl, { headers: this.headers }).pipe(
            map(response => {
                const result = response;
                const cleanedSchemas: Record<string, any> = {};
                for (const key in response.result) {
                    if (response.result.hasOwnProperty(key)) {
                        const cleanedJson = response.result[key].replace(/\\n/g, '').replace(/\\\\/g, '\\');

                        try {
                            cleanedSchemas[key] = JSON.parse(cleanedJson);
                        } catch (error) {
                            console.error(`Errore nella parsing di JSON per la chiave ${key}:`, error);
                            cleanedSchemas[key] = null; // O gestisci l'errore come necessario
                        }
                    }
                }

                result.result = cleanedSchemas;
                return result;
            }),
            catchError(this.handleError)
        );
    }


    public validateFlowTypeGet(flowTypeCode: FlowType['code']): Observable<ActionResult<Array<string>>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'validate/' + flowTypeCode);
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        return this._http.get<ActionResult<Array<string>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
    }

    // POST
    public getFlowType(flowTypeCode: FlowType['code']): Observable<ActionResult<FlowDto>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'read/' + flowTypeCode);
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        return this._http.post<ActionResult<FlowDto>>(dataUrl, null, { headers: this.headers }).pipe(catchError(this.handleError));
    }

    public testEvent(flowTypeCode: FlowType['code'], data: FlowTestObject): Observable<ActionResult<FlowTestInstance>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'testEvents/' + flowTypeCode);
        console.log(JSON.stringify(data));
        return this._http.post<ActionResult<FlowTestInstance>>(dataUrl, data, { headers: this.headers }).pipe(catchError(this.handleError));
    }


    // Risultati di getDrawFlow = { ft: {}, closures: {}, drawFlowData: {}}
    public getDrawFlow(flowTypeCode: FlowType['code']): Observable<ActionResult<DrawFlowFullData>> {
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'read/' + flowTypeCode);

        return this._http.post<ActionResult<FlowDto>>(dataUrl, null, { headers: this.headers }).pipe(
            map(response => {
                // Crea un nuovo oggetto DrawFlowDto
                let data: DrawFlowFullData = new DrawFlowFullData();
                let drawFlowData = this.stepsToDrawFlow(response.result.steps);

                data = {
                    ft: response.result.ft,
                    closures: response.result.closures,
                    drawFlowData: drawFlowData
                };

                let result = new ActionResult<DrawFlowFullData>();
                result.duration = response.duration;
                result.message = response.message;
                result.start = response.start;
                result.status = response.status;
                result.result = data;

                return result;
            }),
            catchError(this.handleError)
        );
    }


    public storeFlowType(dto: FlowDto | DrawFlowFullData): Observable<ActionResult<Array<string>>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'store');
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        if ('drawFlowData' in dto) {
            // è un DrawFlowFullData
            dto = { ft: dto.ft, closures: dto.closures, steps: this.drawFlowToSteps(dto.drawFlowData) };
        }
        return this._http.post<ActionResult<Array<string>>>(dataUrl, dto, { headers: this.headers }).pipe(catchError(this.handleError));
    }

    public validateFlowTypePost(dto: FlowDto | DrawFlowFullData): Observable<ActionResult<FlowValidation>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'validate');
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        if ('drawFlowData' in dto) {
            // è un DrawFlowFullData
            dto = { ft: dto.ft, closures: dto.closures, steps: this.drawFlowToSteps(dto.drawFlowData) };
        }
        return this._http.post<ActionResult<FlowValidation>>(dataUrl, dto, { headers: this.headers }).pipe(catchError(this.handleError));
    }

    // DELETE 
    public deleteFlowType(flowTypeCode: FlowType['code']): Observable<ActionResult<boolean>> {
        const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'delete/' + flowTypeCode);
        this._bowmanCacheService.flushServiceCacheByMethod('listFlowTypes');
        return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
    }



    // UTILITIES

    public stepsToDrawFlow(steps: Record<string, StepType>): DrawFlowDto {
        // Mappa per tenere traccia degli ID dei nodi
        const nodeMap = {};
        let nodeIdCounter = 0; // Contatore per ID dei nodi 
        let drawFlowData: DrawFlowDto = new DrawFlowDto();

        // Itera attraverso le chiavi dell'oggetto steps
        for (const key in steps) {
            const step = steps[key];
            var jsonData;

            if (step.lineage == null) {
                step.lineage = { sourceType: '', jsonData: {} }
            } else {
                if (step.lineage.jsonData == '{}') {
                    step.lineage.jsonData = {};
                } else {
                    jsonData = JSON.parse(step.lineage.jsonData);
                    step.lineage = {
                        sourceType: step.lineage.sourceType, jsonData: jsonData
                    }
                }
            }
            const nodeId = HelperFunctions.uuid(); // ID del nodo (incrementa il contatore)
            step.id = nodeId;

            nodeMap[nodeId] = nodeId; // Mappa il codice del passo all'ID

            let nodeClass = 'dbclick';

            if (step.parents.length == 0 && nodeIdCounter == 1) {
                //nodeClass = nodeClass + ' first';
            }

            let html = '<div><div class="title-box">' + step.name + ' </div>';
            html += '<div class="box">' + (step.description != null && step.description.trim() != '' ? this._trimPipe.transform(step.description, 60) : '') + '</div>';
            html = html + '</div>';

            // Crea il nodo
            drawFlowData[nodeId] = {
                id: nodeId,
                name: step.name,
                data: step,
                class: nodeClass,
                html: html,
                typenode: false,
                inputs: {},
                outputs: {},
                pos_x: 0,
                pos_y: 0
            };
        }


        // Aggiungi le connessioni  
        for (const key in steps) {
            const step = steps[key];
            const currentNodeId = step.id;

            // Inizializza gli input e output 
            const inputKey = `input_1`;
            const outputKey = `output_1`;

            if (!drawFlowData[currentNodeId].inputs[inputKey]) {
                drawFlowData[currentNodeId].inputs[inputKey] = {
                    connections: []
                };
            }
            if (!drawFlowData[currentNodeId].outputs[outputKey]) {
                drawFlowData[currentNodeId].outputs[outputKey] = {
                    connections: []
                };
            }

            if (step.parents.length > 0) {
                step.parents.forEach((parentCode) => {
                    parentCode = this.getNodeIdByCode(drawFlowData, parentCode);
                    const parentNodeId = nodeMap[parentCode];
                    // Aggiungi output al nodo genitore
                    const outputKey = `output_1`;
                    const inputKey = `input_1`;
                    if (!drawFlowData[parentNodeId].outputs[outputKey]) {
                        drawFlowData[parentNodeId].outputs[outputKey] = {
                            connections: []
                        };
                    }
                    // Inizializza labelNode come stringa vuota
                    let labelNode = '<span></span>';

                    // Aggiungi la connessione al nodo genitore
                    drawFlowData[parentNodeId].outputs[outputKey].connections.push({
                        node: currentNodeId,
                        output: inputKey,
                        labels: [new Date().getTime()] // Non mostrare labelOutput
                    });

                    if (!drawFlowData[currentNodeId].inputs[inputKey]) {
                        drawFlowData[currentNodeId].inputs[inputKey] = {
                            connections: []
                        };
                    }
                    drawFlowData[currentNodeId].inputs[inputKey].connections.push({
                        node: parentNodeId,
                        input: outputKey,
                        labels: [labelNode] // Usa labelNode qui, che può essere vuoto
                    });
                });
            } else {
                if (drawFlowData[currentNodeId]) {
                    Object.values(steps).filter(step => step.parents.length > 0).forEach(stepWithParents => {
                        if (stepWithParents.parents.includes(currentNodeId)) {
                            const outputKey = `output_1`;

                            // Assicurati che inputs e outputs siano inizializzati
                            if (!drawFlowData[currentNodeId].inputs) {
                                drawFlowData[currentNodeId].inputs = {};
                            }

                            if (!drawFlowData[currentNodeId].outputs) {
                                drawFlowData[currentNodeId].outputs = {};
                            }

                            // Assicurati che l'outputKey esista in outputs
                            if (!drawFlowData[currentNodeId].outputs[outputKey]) {
                                drawFlowData[currentNodeId].outputs[outputKey] = {
                                    connections: []
                                };
                            }

                            // Aggiungi la connessione
                            drawFlowData[currentNodeId].outputs[outputKey].connections.push({
                                node: stepWithParents.code,
                                input: outputKey,
                                labels: [] // Usa labelNode qui, che può essere vuoto
                            });
                        }
                    });

                }
            }

        }

        return drawFlowData;
    }

    public drawFlowToSteps(drawflow: DrawFlowDto): Record<string, StepType> {
        const steps: Record<string, StepType> = {};

        for (const nodeId in drawflow) {
            const node = drawflow[nodeId];

            if (node.data.lineage != null && node.data.lineage?.sourceType != null) {
                if (node.data.lineage.sourceType == '') {
                    node.data.lineage = null;
                }

                if (node.data.lineage && node.data.lineage.jsonData != null) {
                    if (node.data.lineage.jsonData) {
                        console.log(JSON.stringify(node.data.lineage.jsonData));
                        node.data.lineage.jsonData = JSON.stringify(node.data.lineage.jsonData);
                    }
                }
            } else {
                node.data.lineage = null;
            }

            // Crea un nuovo step
            const step: StepType = { // Aggiungi il tempo dell'ultimo aggiornamento
                ...node.data,
                parents: [],
                lastUpdateTime: new Date().getTime()
            };

            // Aggiungi i codici dei nodi genitori dagli input
            for (const inputKey in node.inputs) {
                const input = node.inputs[inputKey];
                input.connections.forEach(connection => {
                    const parentNodeId = connection.node.toString(); // Converti in stringa
                    step.parents.push(drawflow[parentNodeId].data.code); // Aggiungi il codice del nodo genitore
                });
            }

            // Aggiungi il passo all'oggetto steps con nodeId come chiave
            steps[node.data.code] = step; // Usa il codice come chiave
        }

        return steps; // Restituisce l'oggetto con la chiave "steps" 
    }

    private getNodeIdByCode(drawflow: DrawFlowDto, code: string): string | undefined {
        for (const key in drawflow) {
            if (drawflow[key].data.code === code) {
                return drawflow[key].id; // Restituisce l'id se il code corrisponde
            }
        }
        return null;
    }


    private handleError(error: any) {
        let parsedError: any;
        if (error && error.error && error.error.message) {
            parsedError = error.error.message;
        } else if (error && error.message) {
            parsedError = error.message;
        } else if (error && error._body) {
            parsedError = error._body;
        } else {
            parsedError = 'Flow Editor Service Error';
        }
        return observableThrowError(parsedError);
    }

}
